/**
 * Employee Service
 * Handles CRUD operations for employees using Supabase backend
 */
import supabaseService from './supabaseService';
import logger from '../utils/logger';
import { hashPassword } from '../utils/passwordUtils';
import { v4 as uuidv4 } from 'uuid';
import emailService from './emailService';

const EMPLOYEES_TABLE = 'employees';

class EmployeeService {
  async getAllEmployees() {
    try {
      logger.info('Fetching all employees', { operation: 'getAllEmployees' });

      const employees = await supabaseService.select(EMPLOYEES_TABLE, {
        order: { column: 'createdAt', ascending: true },
      });

      logger.info('Employees fetched successfully', {
        operation: 'getAllEmployees',
        count: employees?.length || 0,
      });

      return employees || [];
    } catch (error) {
      logger.error('Error fetching employees', error, { operation: 'getAllEmployees' });
      throw error;
    }
  }

  async getEmployeeById(employeeId) {
    try {
      logger.info('Fetching employee by ID', { employeeId, operation: 'getEmployeeById' });

      const employee = await supabaseService.select(EMPLOYEES_TABLE, {
        filters: [{ column: 'id', value: employeeId }],
        single: true,
      });

      if (!employee) {
        logger.warning('Employee not found', { employeeId, operation: 'getEmployeeById' });
        return null;
      }

      logger.info('Employee fetched successfully', { employeeId, operation: 'getEmployeeById' });
      return employee;
    } catch (error) {
      logger.error('Error fetching employee', error, { employeeId, operation: 'getEmployeeById' });
      throw error;
    }
  }

  async createEmployee(employeeData) {
    try {
      logger.info('Creating new employee', { operation: 'createEmployee', data: employeeData });

      if (!employeeData.name || !employeeData.email || !employeeData.role) {
        throw new Error('Missing required fields: name, email, role');
      }

      const existingEmployee = await supabaseService.select(EMPLOYEES_TABLE, {
        filters: [{ column: 'email', value: employeeData.email }],
        single: true,
      });

      if (existingEmployee) {
        throw new Error('Employee with this email already exists');
      }

      let passwordHash = null;
      if (employeeData.password) {
        passwordHash = await hashPassword(employeeData.password);
      }

      const timestamp = new Date().toISOString();
      const newEmployee = {
        id: uuidv4(),
        name: employeeData.name,
        email: employeeData.email,
        role: employeeData.role,
        managerId: employeeData.managerId || null,
        centerId: employeeData.centerId || null,
        yearlyTokens: employeeData.yearlyTokens || 12,
        tokensUsed: 0,
        passwordHash,
        createdAt: timestamp,
        updatedAt: timestamp,
      };

      const inserted = await supabaseService.insert(EMPLOYEES_TABLE, newEmployee);
      const createdEmployee = Array.isArray(inserted) ? inserted[0] : inserted;

      logger.info('Employee created successfully', {
        employeeId: createdEmployee?.id,
        operation: 'createEmployee',
      });

      try {
        const tempPassword = employeeData.password || null;
        await emailService.sendWelcomeEmail(createdEmployee || newEmployee, tempPassword);
      } catch (emailError) {
        logger.warning('Failed to send welcome email', {
          employeeId: newEmployee.id,
          error: emailError?.message,
        });
      }

      return createdEmployee || newEmployee;
    } catch (error) {
      logger.error('Error creating employee', error, { operation: 'createEmployee' });
      throw error;
    }
  }

  async updateEmployee(employeeId, updates) {
    try {
      logger.info('Updating employee', { employeeId, updates, operation: 'updateEmployee' });

      const currentEmployee = await this.getEmployeeById(employeeId);
      if (!currentEmployee) {
        throw new Error('Employee not found');
      }

      if (
        updates.email &&
        updates.email !== currentEmployee.email
      ) {
        const existing = await supabaseService.select(EMPLOYEES_TABLE, {
          filters: [
            { column: 'email', value: updates.email },
            { column: 'id', operator: 'neq', value: employeeId },
          ],
        });

        if (Array.isArray(existing) && existing.length > 0) {
          throw new Error('Email already in use by another employee');
        }
      }

      const updateData = { ...updates };
      if (updates.password) {
        updateData.passwordHash = await hashPassword(updates.password);
        delete updateData.password;
      }

      updateData.updatedAt = new Date().toISOString();

      const updatedEmployee = await supabaseService.update(EMPLOYEES_TABLE, updateData, {
        filters: [{ column: 'id', value: employeeId }],
        single: true,
      });

      if (!updatedEmployee) {
        throw new Error('Employee not found');
      }

      logger.info('Employee updated successfully', { employeeId, operation: 'updateEmployee' });

      return updatedEmployee;
    } catch (error) {
      logger.error('Error updating employee', error, { employeeId, operation: 'updateEmployee' });
      throw error;
    }
  }

  async deleteEmployee(employeeId) {
    try {
      logger.info('Deleting employee', { employeeId, operation: 'deleteEmployee' });

      const deleted = await supabaseService.remove(EMPLOYEES_TABLE, {
        filters: [{ column: 'id', value: employeeId }],
        single: true,
      });

      if (!deleted) {
        throw new Error('Employee not found');
      }

      logger.info('Employee deleted successfully', { employeeId, operation: 'deleteEmployee' });
      return true;
    } catch (error) {
      logger.error('Error deleting employee', error, { employeeId, operation: 'deleteEmployee' });
      throw error;
    }
  }

  async getEmployeesByManager(managerId) {
    try {
      logger.info('Fetching employees by manager', { managerId, operation: 'getEmployeesByManager' });

      if (!managerId) {
        return [];
      }

      const teamMembers = await supabaseService.select(EMPLOYEES_TABLE, {
        filters: [{ column: 'managerId', value: managerId }],
        order: { column: 'name', ascending: true },
      });

      logger.info('Team members fetched', {
        managerId,
        count: teamMembers?.length || 0,
        operation: 'getEmployeesByManager',
      });

      return teamMembers || [];
    } catch (error) {
      logger.error('Error fetching team members', error, {
        managerId,
        operation: 'getEmployeesByManager',
      });
      throw error;
    }
  }

  async updateTokenUsage(employeeId, tokensUsed) {
    try {
      logger.info('Updating token usage', {
        employeeId,
        tokensUsed,
        operation: 'updateTokenUsage',
      });

      const updated = await supabaseService.update(EMPLOYEES_TABLE, {
        tokensUsed,
        updatedAt: new Date().toISOString(),
      }, {
        filters: [{ column: 'id', value: employeeId }],
        single: true,
      });

      if (!updated) {
        throw new Error('Employee not found');
      }

      return updated;
    } catch (error) {
      logger.error('Error updating token usage', error, {
        employeeId,
        operation: 'updateTokenUsage',
      });
      throw error;
    }
  }

  async recalculateTokenUsage(employeeId, requests) {
    try {
      logger.info('Recalculating token usage', {
        employeeId,
        operation: 'recalculateTokenUsage',
      });

      // Count approved requests AND cancellation_requested requests that were originally approved
      // Tokens should remain deducted until cancellation is actually approved
      const approvedRequests = requests.filter(
        (req) => req.employeeId === employeeId && (
          req.status === 'approved' || 
          (req.status === 'cancellation_requested' && req.originalStatus === 'approved')
        )
      );

      const totalTokensUsed = approvedRequests.reduce((sum, req) => sum + (req.days || 1), 0);

      logger.info('Token usage recalculated', {
        employeeId,
        totalTokensUsed,
        approvedRequestsCount: approvedRequests.length,
        operation: 'recalculateTokenUsage',
      });

      return await this.updateTokenUsage(employeeId, totalTokensUsed);
    } catch (error) {
      logger.error('Error recalculating token usage', error, {
        employeeId,
        operation: 'recalculateTokenUsage',
      });
      throw error;
    }
  }

  async resetPassword(employeeId, newPassword) {
    try {
      logger.info('Resetting employee password', { employeeId, operation: 'resetPassword' });

      if (!newPassword || newPassword.trim().length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      const passwordHash = await hashPassword(newPassword);

      const updatedEmployee = await supabaseService.update(EMPLOYEES_TABLE, {
        passwordHash,
        updatedAt: new Date().toISOString(),
      }, {
        filters: [{ column: 'id', value: employeeId }],
        single: true,
      });

      if (!updatedEmployee) {
        throw new Error('Employee not found');
      }

      try {
        await emailService.sendPasswordResetEmail(updatedEmployee);
      } catch (emailError) {
        logger.warning('Failed to send password reset email', {
          employeeId,
          error: emailError?.message,
        });
      }

      return updatedEmployee;
    } catch (error) {
      logger.error('Error resetting password', error, { employeeId, operation: 'resetPassword' });
      throw error;
    }
  }
}

const employeeService = new EmployeeService();
export default employeeService;

